import React, { useEffect, useRef, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { BrandMark } from './BrandMark';

type LandingHeaderProps = {
  onLogin: () => void;
  onRegister: () => void;
};

const items = [
  { label: 'Plattformen', href: '#plattformen' },
  { label: 'Föraren', href: '#foraren' },
  { label: 'Tre roller', href: '#tre-ytor' },
  { label: 'Frågor', href: '#faq' },
  { label: 'Lag & kontroll', href: '#lag-kontroll' },
];

export function LandingHeader({ onLogin, onRegister }: LandingHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMobileLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      firstMobileLinkRef.current?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  function closeMenu() {
    setIsOpen(false);
  }

  return (
    <header
      className={`sticky top-0 z-50 h-[72px] border-b bg-[#F4F8FA]/88 backdrop-blur-xl transition-[box-shadow,border-color,background-color] duration-300 ${isScrolled ? 'border-[#06B6D4]/20 shadow-[0_8px_24px_rgba(11,17,32,0.06)]' : 'border-[#06B6D4]/10'}`}
    >
      <div className="mx-auto flex h-full max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <a href="#top" className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]">
          <BrandMark compact />
        </a>
        <nav className="hidden items-center gap-5 lg:flex" aria-label="Huvudnavigering">
          {items.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="group relative py-2 text-sm font-semibold text-slate-700 transition-colors hover:text-[#007EAF] focus:outline-none focus-visible:text-[#007EAF]"
            >
              {item.label}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-0.5 left-0 h-0.5 w-full origin-left scale-x-0 rounded-full bg-[#06B6D4] transition-transform duration-300 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100"
              />
            </a>
          ))}
          <span className="h-4 w-px bg-[#0B1120]/10" aria-hidden="true" />
          <button
            type="button"
            onClick={onLogin}
            className="min-h-10 px-1 text-sm font-bold text-[#0B1120] hover:text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
          >
            Logga in
          </button>
          <motion.button
            type="button"
            onClick={onRegister}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.14 }}
            className="min-h-10 rounded-lg bg-[#0B1120] px-3.5 text-sm font-bold text-white hover:bg-[#163049] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-2"
          >
            Registrera taxiföretag
          </motion.button>
        </nav>
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className="grid h-10 w-10 place-items-center rounded-lg text-[#0B1120] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] lg:hidden"
          aria-label={isOpen ? 'Stäng meny' : 'Öppna meny'}
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
        >
          {isOpen ? <XIcon size={20} aria-hidden="true" /> : <MenuIcon size={20} aria-hidden="true" />}
        </button>
      </div>
      <AnimatePresence>
        {isOpen ? (
          <motion.nav
            id="mobile-navigation"
            aria-label="Mobilnavigering"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="border-b border-[#0B1120]/10 bg-[#F4F8FA] shadow-[0_8px_18px_rgba(11,17,32,0.06)] lg:hidden"
          >
            <div className="mx-auto flex max-w-6xl flex-col px-5 py-2 sm:px-6">
              {items.map((item, index) => (
                <a
                  key={item.href}
                  ref={index === 0 ? firstMobileLinkRef : undefined}
                  href={item.href}
                  onClick={closeMenu}
                  className="border-b border-[#0B1120]/10 py-3.5 text-sm font-medium text-[#0B1120] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
                >
                  {item.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onLogin();
                }}
                className="py-3.5 text-left text-sm font-semibold text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
              >
                Logga in
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  onRegister();
                }}
                className="mb-2 min-h-10 rounded-lg bg-[#0B1120] px-4 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
              >
                Registrera taxiföretag
              </button>
            </div>
          </motion.nav>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
